import { useMemo } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { useStableNavigate } from '@/shared/lib/stable-navigate'
import { useNetworkStatusStore } from '@/shared/stores/network-status.store'

export const OFFLINE_NAVIGATE_BLOCKED_EVENT = 'app:offline-navigate-blocked'
export const CHUNK_LOAD_FAILED_EVENT = 'app:chunk-load-failed'

export interface OfflineNavigateBlockedDetail {
  to: unknown
  options?: unknown
  /** Retry the original navigation after the user goes back online */
  retry: () => void
}

/**
 * Drop-in replacement for `useStableNavigate` that adds offline-awareness:
 * when `navigator.onLine` is false at call time, the navigate call is
 * intercepted and the global OfflineModal is opened with a retry callback.
 * When the connection is restored, the modal invokes the retry, which
 * re-runs the original navigation as if the user had clicked just now.
 *
 * **This is the default navigate hook for app code** (features, widgets,
 * pages). Reach for `useStableNavigate` directly only inside shared
 * utilities that should NOT trigger the offline gate (e.g. interceptor-
 * driven redirects in `useMultipleQueries`, internal route helpers).
 *
 * Internally calls {@link useStableNavigate} so the returned function is
 * referentially stable across renders — safe to depend on inside
 * `useEffect` / `useCallback` / `useMemo`.
 *
 * Reads `navigator.onLine` via the Zustand store so the check is
 * synchronous and doesn't need to subscribe/re-render the component.
 */
export function useSafeNavigate(): NavigateFunction {
  const navigate = useStableNavigate()

  // NavigateFunction has overloads: (to, options?) | (delta). We wrap with
  // variadic args and forward-apply to preserve both signatures.
  // useMemo keeps the reference stable while satisfying react-compiler
  // (useCallback requires an inline function expression).
  return useMemo(() => {
    const fn = (...args: unknown[]) => {
      const isOnline = useNetworkStatusStore.getState().isOnline
      if (!isOnline) {
        const retry = () => (navigate as (...a: unknown[]) => void)(...args)
        window.dispatchEvent(
          new CustomEvent<OfflineNavigateBlockedDetail>(OFFLINE_NAVIGATE_BLOCKED_EVENT, {
            detail: { to: args[0], options: args[1], retry },
          }),
        )
        return
      }
      ;(navigate as (...a: unknown[]) => void)(...args)
    }
    return fn as unknown as NavigateFunction
  }, [navigate])
}
